/**
 * SCG Source Viewer — displays a rule's source PDF with the rule text
 * highlighted via the react-pdf text layer.
 *
 * Started life as the react-pdf evaluation sandbox (route kept as
 * /pdf-sandbox for link compatibility); now the production viewer behind
 * every ViewInPdfButton. Worker pattern mirrors
 * components/redaction/PDFRedactionEditor.js, already in production for
 * FOIA redaction.
 *
 * Query params: ?pdf=<url>&page=N&ruleId=&title=&level=&q=
 * (legacy ?bbox=/&matchScore= params are ignored — the bbox overlay was
 * removed; text-search highlighting is the only strategy.)
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/Page/TextLayer.css';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import { useSearchParams } from 'react-router-dom';
import {
  MagnifyingGlassIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { getSCGRuleSourceInfo } from '../services/api';
import { PDFJS_DOCUMENT_OPTIONS } from '../utils/pdfjsOptions';

pdfjs.GlobalWorkerOptions.workerSrc = '/static/js/pdf.worker.min.js';

// IC banner colors keyed by normalized classification level.
const LEVEL_COLORS = {
  'TOP SECRET': '#ff8c00',
  SECRET: '#c8102e',
  CONFIDENTIAL: '#0033a0',
  UNCLASSIFIED: '#007a33',
};

// Pick a short distinctive phrase to seed the search box. PDF text-layer
// spans break on word groups, so multi-line or long queries rarely match a
// single span — the seed is a hint the user can refine.
const STOPWORDS = new Set([
  'a', 'an', 'the', 'of', 'in', 'on', 'at', 'to', 'for', 'and', 'or',
  'but', 'with', 'by', 'from', 'as', 'is', 'are', 'was', 'were',
]);

function deriveSearchQuery(rawText) {
  if (!rawText) return '';
  const firstLine = rawText.split(/\n+/).map(s => s.trim()).find(s => s.length > 0) || '';
  const words = firstLine.split(/\s+/).filter(Boolean);
  let i = 0;
  while (i < words.length && STOPWORDS.has(words[i].toLowerCase())) i++;
  const phrase = words.slice(i, i + 5).join(' ');
  if (phrase.length <= 40) return phrase;
  const cut = phrase.slice(0, 40);
  const lastSpace = cut.lastIndexOf(' ');
  return lastSpace > 15 ? cut.slice(0, lastSpace) : cut;
}

const PDFViewerSandbox = () => {
  const [searchParams] = useSearchParams();
  const pdfUrl = searchParams.get('pdf');

  // R2-AUTH: backend PDF routes (e.g. /api/v1/foia/pdf/{hash}) now require a
  // Bearer token. react-pdf fetches the URL itself, so pass httpHeaders via
  // the file spec. Memoized — a fresh object each render makes react-pdf
  // reload the document in a loop.
  const pdfFileSpec = useMemo(() => {
    if (!pdfUrl) return null;
    const token = localStorage.getItem('authToken');
    return token
      ? { url: pdfUrl, httpHeaders: { Authorization: `Bearer ${token}` } }
      : pdfUrl;
  }, [pdfUrl]);
  const initialPage = parseInt(searchParams.get('page') || '1', 10);

  // Rule context (passed from the citation link so no fetch is required
  // to render; the fetch below only fills in the source filename).
  const ruleId = searchParams.get('ruleId');
  const ruleTitle = searchParams.get('title') || '';
  const ruleLevel = (searchParams.get('level') || '').toUpperCase();
  const initialQuery = searchParams.get('q') || '';
  const inRuleMode = Boolean(ruleId);

  const [numPages, setNumPages] = useState(null);
  const [currentPage, setCurrentPage] = useState(
    Number.isFinite(initialPage) && initialPage > 0 ? initialPage : 1
  );
  const [scale, setScale] = useState(1.2);
  const [loadError, setLoadError] = useState(null);
  const [docName, setDocName] = useState('');
  const pageWidthRef = useRef(null);   // page width in PDF points at scale 1
  const canvasRef = useRef(null);      // scrollable canvas element

  // Text-search highlight — pre-populated from ?q= when arriving from a rule.
  const [searchText, setSearchText] = useState(deriveSearchQuery(initialQuery));
  const [matchCount, setMatchCount] = useState(0);
  const matchCountRef = useRef(0);

  // The source filename isn't in the URL; fetch it from the rule when we can.
  useEffect(() => {
    let cancelled = false;
    if (!ruleId) return undefined;
    getSCGRuleSourceInfo(ruleId)
      .then((info) => {
        if (cancelled) return;
        const name = info?.source_document?.filename
          || info?.rule_details?.source_document?.filename;
        if (name) setDocName(name.replace(/\.pdf$/i, ''));
      })
      .catch(() => { /* filename is cosmetic — fall back to the generic title */ });
    return () => { cancelled = true; };
  }, [ruleId]);

  const handleDocLoadSuccess = ({ numPages: n }) => {
    setNumPages(n);
    setLoadError(null);
    setCurrentPage((p) => Math.min(Math.max(1, p), n));
  };

  const handleDocLoadError = (err) => {
    setLoadError(err?.message || String(err));
  };

  const handlePageLoadSuccess = (page) => {
    pageWidthRef.current = page.originalWidth || page.width / (scale || 1);
  };

  const goToPage = (p) => {
    const clamped = Math.min(Math.max(1, p), numPages || p);
    setCurrentPage(Number.isFinite(clamped) ? clamped : 1);
  };

  const fitWidth = () => {
    const container = canvasRef.current;
    const pageWidth = pageWidthRef.current;
    if (!container || !pageWidth) return;
    const available = container.clientWidth - 48; // canvas padding
    setScale(Math.max(0.5, Math.min(3, available / pageWidth)));
  };

  // Wrap matching text-layer spans in <mark>; count matches per page.
  matchCountRef.current = 0;
  const customTextRenderer = useCallback(
    ({ str }) => {
      if (!searchText) return str;
      const escaped = searchText.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      const re = new RegExp(`(${escaped})`, 'gi');
      const matches = str.match(re);
      if (matches) matchCountRef.current += matches.length;
      return str.replace(
        re,
        '<mark style="background:rgba(252,211,77,0.7);color:inherit;padding:0;border-radius:2px">$1</mark>'
      );
    },
    [searchText]
  );

  const handleRenderTextLayerSuccess = () => {
    setMatchCount(matchCountRef.current);
  };

  const documentOptions = PDFJS_DOCUMENT_OPTIONS;

  const levelColor = LEVEL_COLORS[ruleLevel] || '#475569';

  // ── Empty state: no document in the URL ────────────────────────────────
  if (!pdfUrl) {
    return (
      <div className="h-screen flex items-center justify-center bg-slate-700">
        <div className="bg-white rounded-lg shadow-xl px-8 py-6 max-w-md text-center">
          <h1 className="text-base font-semibold text-slate-900 mb-1">
            No document to show
          </h1>
          <p className="text-sm text-slate-600">
            Open this viewer from a rule citation or a source-document link.
            The link supplies which PDF and page to display.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen flex flex-col bg-slate-700">
      {/* Toolbar */}
      <div className="bg-white border-b border-slate-200 px-3.5 py-2 flex items-center gap-4 flex-wrap">
        <div className="min-w-0 flex-1" style={{ flexBasis: '240px' }}>
          <div className="text-sm font-semibold text-slate-900 truncate">
            {docName || 'SCG source document'}
          </div>
          <div className="text-[11px] text-slate-400">
            {numPages ? `${numPages} page${numPages === 1 ? '' : 's'}` : 'Loading…'}
          </div>
        </div>

        {/* Page navigation */}
        <div className="flex items-center gap-1.5 flex-shrink-0">
          <button
            className="p-1.5 border border-slate-200 bg-slate-50 hover:bg-slate-100 rounded-md text-slate-600"
            onClick={() => goToPage(currentPage - 1)}
            aria-label="Previous page"
          >
            <ChevronLeftIcon className="w-4 h-4" />
          </button>
          <input
            type="number"
            value={currentPage}
            min={1}
            max={numPages || 999}
            onChange={(e) => goToPage(parseInt(e.target.value || '1', 10))}
            className="w-12 px-1 py-1 border border-slate-200 rounded-md text-sm text-center tabular-nums"
            aria-label="Page number"
          />
          <span className="text-xs text-slate-400 whitespace-nowrap">
            of {numPages || '…'}
          </span>
          <button
            className="p-1.5 border border-slate-200 bg-slate-50 hover:bg-slate-100 rounded-md text-slate-600"
            onClick={() => goToPage(currentPage + 1)}
            aria-label="Next page"
          >
            <ChevronRightIcon className="w-4 h-4" />
          </button>
        </div>

        {/* Zoom */}
        <div className="flex items-center gap-1.5 flex-shrink-0">
          <button
            className="px-2.5 py-1 border border-slate-200 bg-slate-50 hover:bg-slate-100 rounded-md text-sm text-slate-600"
            onClick={() => setScale((s) => Math.max(0.5, Math.round((s - 0.1) * 10) / 10))}
            aria-label="Zoom out"
          >
            −
          </button>
          <span className="text-xs text-slate-500 w-10 text-center tabular-nums">
            {Math.round(scale * 100)}%
          </span>
          <button
            className="px-2.5 py-1 border border-slate-200 bg-slate-50 hover:bg-slate-100 rounded-md text-sm text-slate-600"
            onClick={() => setScale((s) => Math.min(3, Math.round((s + 0.1) * 10) / 10))}
            aria-label="Zoom in"
          >
            +
          </button>
          <button
            className="px-2.5 py-1 border border-slate-200 bg-slate-50 hover:bg-slate-100 rounded-md text-xs text-slate-600"
            onClick={fitWidth}
          >
            Fit width
          </button>
        </div>

        {/* Search */}
        <div className="flex items-center gap-1.5 border border-slate-200 bg-slate-50 rounded-lg px-2 py-1 flex-shrink-0">
          <MagnifyingGlassIcon className="w-3.5 h-3.5 text-slate-400" />
          <input
            type="text"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            placeholder="Search in document"
            className="bg-transparent outline-none text-sm w-44 text-slate-800"
          />
          {searchText && (
            <span className="text-[11px] text-slate-600 bg-amber-100 rounded px-1.5 whitespace-nowrap">
              {matchCount} on this page
            </span>
          )}
        </div>

        <button
          className="px-3 py-1.5 border border-slate-200 bg-slate-50 hover:bg-slate-100 rounded-md text-sm text-slate-600 flex-shrink-0"
          onClick={() => window.close()}
        >
          Close
        </button>
      </div>

      {/* Rule context strip */}
      {inRuleMode && (
        <div className="bg-slate-50 border-b border-slate-200 px-3.5 py-1.5 flex items-center gap-2.5 text-xs">
          <span className="font-mono font-semibold text-red-700">{ruleId}</span>
          {ruleLevel && (
            <span
              className="px-1.5 py-0.5 rounded font-bold tracking-wide text-white text-[10px]"
              style={{ background: levelColor }}
            >
              {ruleLevel}
            </span>
          )}
          {ruleTitle && (
            <span className="text-slate-600 truncate min-w-0 flex-1">{ruleTitle}</span>
          )}
          {searchText && (
            <span className="flex items-center gap-1 text-slate-600 bg-amber-50 border border-amber-200 rounded px-2 py-0.5 whitespace-nowrap">
              highlighting rule text
              <button
                onClick={() => setSearchText('')}
                className="text-slate-400 hover:text-slate-600"
                aria-label="Clear highlight"
              >
                <XMarkIcon className="w-3 h-3" />
              </button>
            </span>
          )}
        </div>
      )}

      {/* Document canvas */}
      <div ref={canvasRef} className="flex-1 overflow-auto p-6 flex justify-center items-start">
        {loadError ? (
          <div className="bg-white rounded-lg shadow-xl px-8 py-6 max-w-md text-center mt-10">
            <h2 className="text-base font-semibold text-slate-900 mb-1">
              Couldn&apos;t load the document
            </h2>
            <p className="text-sm text-slate-600 break-all">{loadError}</p>
          </div>
        ) : (
          <div className="bg-white shadow-2xl">
            <Document
              file={pdfFileSpec}
              onLoadSuccess={handleDocLoadSuccess}
              onLoadError={handleDocLoadError}
              options={documentOptions}
              loading={<div className="p-10 text-slate-300">Loading document…</div>}
              error={<div className="p-10 text-red-500">Failed to load PDF.</div>}
            >
              <Page
                pageNumber={currentPage}
                scale={scale}
                renderTextLayer={true}
                renderAnnotationLayer={false}
                customTextRenderer={customTextRenderer}
                onRenderTextLayerSuccess={handleRenderTextLayerSuccess}
                onLoadSuccess={handlePageLoadSuccess}
              />
            </Document>
          </div>
        )}
      </div>
    </div>
  );
};

export default PDFViewerSandbox;
